import * as React from 'react'

import { CLIENT_CALLS_PAGE_SIZE } from '@/config/constants'
import type { CallRecord } from '@/features/calls/types'
import { useCapability } from '@/features/permissions/hooks'
import { useCallsCountQuery } from '@/hooks/useCallsCountQuery'
import { useCallsQuery } from '@/hooks/useCallsQuery'
import type { FilterCondition } from '@/utils/filterUtils'
import type { ClientDuty } from './useClientDutiesQuery'
import { useClientDutiesQuery } from './useClientDutiesQuery'
import { NO_ROWS } from './useClientHistory'

export interface ClientCalls {
  allowed: boolean
  isLoading: boolean
  isError: boolean
  /** Newest first; more pages load on request. */
  rows: CallRecord[]
  /** Every call on the client's record, or null until the count loads. */
  total: number | null
  hasMore: boolean
  isLoadingMore: boolean
  loadMore: () => void
}

export interface ClientDuties {
  isLoading: boolean
  isError: boolean
  rows: ClientDuty[]
}

export interface ClientActivity {
  calls: ClientCalls
  duties: ClientDuties
}

/**
 * Calls with the client, and the duties they are assigned to.
 *
 * Calls need the viewer's call-log capability. Filtering to one patient_id
 * returns the client's whole history: relay skips the calls API's 90-day
 * default for a single client.
 */
export function useClientActivity(
  patientId: string | undefined
): ClientActivity {
  const canCalls = useCapability('calls.list') === true

  const filters = React.useMemo<FilterCondition[]>(
    () =>
      patientId
        ? [
            {
              id: 'patient_id',
              field: 'patient_id',
              operator: '=',
              value: patientId,
            },
          ]
        : [],
    [patientId]
  )

  const enabled = canCalls && !!patientId
  const callsQuery = useCallsQuery(
    {
      filters,
      limit: CLIENT_CALLS_PAGE_SIZE,
      sort_by: 'created_at',
      sort_order: 'desc',
    },
    { enabled }
  )
  const countQuery = useCallsCountQuery({ filters }, { enabled })
  const dutiesQuery = useClientDutiesQuery(patientId)

  const rows = React.useMemo(
    () => callsQuery.data?.pages.flatMap((page) => page.data) ?? NO_ROWS,
    [callsQuery.data]
  )
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = callsQuery
  const callsLoading = enabled && callsQuery.isLoading
  const callsError = callsQuery.isError
  const total = countQuery.data?.count ?? null

  // Memoized field by field so each slice keeps its identity between renders
  // and the memos downstream that key on it hold.
  const calls = React.useMemo<ClientCalls>(
    () => ({
      allowed: canCalls,
      isLoading: callsLoading,
      isError: callsError,
      rows,
      total,
      hasMore: hasNextPage,
      isLoadingMore: isFetchingNextPage,
      loadMore: () => void fetchNextPage(),
    }),
    [
      canCalls,
      callsLoading,
      callsError,
      rows,
      total,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    ]
  )
  const dutyRows = dutiesQuery.data ?? NO_ROWS
  const duties = React.useMemo<ClientDuties>(
    () => ({
      isLoading: dutiesQuery.isLoading,
      isError: dutiesQuery.isError,
      rows: dutyRows,
    }),
    [dutiesQuery.isLoading, dutiesQuery.isError, dutyRows]
  )

  return React.useMemo(() => ({ calls, duties }), [calls, duties])
}
